/**
 * Subscription Success Page — shown after Stripe Checkout completes.
 * Premium, celebratory design that reinforces the value of upgrading.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Sparkles, Rocket, Shield, Zap, Crown } from 'lucide-react';
import { useUser } from '../../../context/UserContext';
import PurchaseCelebration from '../../../components/billing/PurchaseCelebration';
import toast from '../../../utils/toast';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser, fetchUsageSummary, planDisplayName } = useUser();
  const [status, setStatus] = useState('verifying');
  const [showCelebration, setShowCelebration] = useState(false);
  const [resolvedPlan, setResolvedPlan] = useState(null);
  const sessionId = searchParams.get('session_id');
  const retryCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const refreshData = async () => {
      try {
        await Promise.all([fetchUser(), fetchUsageSummary()]);
      } catch {
        // Even if refresh fails, subscription was created via webhook
      }

      // Give webhook a bit more time, then check if plan updated
      // If still on Free after 2 retries, show success anyway
      retryCount.current += 1;
      if (retryCount.current <= 2 && planDisplayName === 'Free') {
        setTimeout(refreshData, 2000);
        return;
      }

      setStatus('success');
      setShowCelebration(true);
    };

    // Initial delay to let webhook process
    const timer = setTimeout(refreshData, 2000);
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture plan name once it changes from Free
  useEffect(() => {
    if (planDisplayName && planDisplayName !== 'Free' && !resolvedPlan) {
      setResolvedPlan(planDisplayName);
    }
  }, [planDisplayName, resolvedPlan]);

  const displayPlan = resolvedPlan || (planDisplayName !== 'Free' ? planDisplayName : null);

  /* ─── Loading ─── */
  if (status === 'verifying') {
    return (
      <div className="flex flex-col flex-1 justify-center items-center w-full bg-gray-50">
        <div className="relative flex justify-center items-center">
          <div className="absolute w-20 h-20 rounded-full bg-blue-100 animate-ping opacity-30" />
          <Loader2 className="relative w-10 h-10 text-blue-600 animate-spin" />
        </div>
        <p className="mt-6 text-sm font-medium text-gray-600">Setting up your subscription...</p>
        <p className="mt-1 text-xs text-gray-400">This only takes a moment</p>
      </div>
    );
  }

  /* ─── Error ─── */
  if (status === 'error') {
    return (
      <div className="flex flex-col flex-1 justify-center items-center w-full bg-gray-50 px-4">
        <div className="text-center max-w-lg">
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <Shield className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">We couldn't verify your session</h1>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            If you completed payment, your subscription will be activated automatically within a few minutes.
            You can check your subscription status in Settings.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/agent/settings?tab=subscription')}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              type="button"
            >
              Check Status
            </button>
            <button
              onClick={() => navigate('/agent/dashboard')}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all"
              type="button"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Success ─── */
  return (
    <div className="relative flex flex-col flex-1 justify-center items-center w-full bg-gray-50 px-4 overflow-hidden">
      {showCelebration && <PurchaseCelebration duration={4500} />}

      <div className="relative z-10 text-center max-w-lg">
        {/* Animated icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200 animate-success-pop">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center border-2 border-white shadow-sm">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {displayPlan ? `Welcome to ${displayPlan}!` : "You're all set!"}
        </h1>
        <p className="text-base text-gray-500 mb-8 leading-relaxed">
          Your subscription is active. You now have access to everything you need to create
          incredible travel experiences for your clients.
        </p>

        {/* Quick value highlights */}
        <div className="grid grid-cols-3 gap-3 mb-8 max-w-md mx-auto">
          <div className="flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
            <Rocket className="w-5 h-5 text-blue-500" />
            <span className="text-[11px] font-medium text-gray-600 leading-tight text-center">Itinerary Generation</span>
          </div>
          <div className="flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
            <Zap className="w-5 h-5 text-amber-500" />
            <span className="text-[11px] font-medium text-gray-600 leading-tight text-center">Client Management</span>
          </div>
          <div className="flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
            <Crown className="w-5 h-5 text-purple-500" />
            <span className="text-[11px] font-medium text-gray-600 leading-tight text-center">Premium Support</span>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={() => {
            toast.success(displayPlan ? `Welcome to ${displayPlan}!` : 'Subscription activated!');
            navigate('/agent/dashboard');
          }}
          className="inline-flex gap-2 items-center px-8 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md shadow-blue-200 hover:shadow-lg hover:shadow-blue-300"
          type="button"
        >
          Start Building Itineraries <ArrowRight className="w-4 h-4" />
        </button>

        <p className="mt-4 text-xs text-gray-400">
          You can manage your subscription anytime in Settings
        </p>
      </div>

      <style>{`
        @keyframes success-pop {
          0% { transform: scale(0.3) rotate(-10deg); opacity: 0; }
          50% { transform: scale(1.15) rotate(3deg); opacity: 1; }
          70% { transform: scale(0.95) rotate(-1deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .animate-success-pop { animation: success-pop 0.6s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default SubscriptionSuccess;
